/**
 * ADR-0185 Phase B — the move popover (never a modal): click a job on the
 * Refined row → choose how it should be placed. The five strategies from the
 * design record, the machine select, and the parameter pickers for the
 * parameterized strategies. Best fit is grayed with "already at its best
 * fit" when it would be a no-op.
 *
 * Approved machines (ADR-0183 Amendment 4) — when the model connects the
 * card, the select offers ONLY the machines the routing approves for the
 * part, in the plant's preference order, and the title says which they are:
 * the picker must say why it offers what it offers. Staying where the book
 * has the job is never a move, so the plan's machine is always offered even
 * when it sits outside the set. Unconnected, the select is every lane and
 * the sentence is the one it always was — the machine is the planner's own
 * call. The option list and the help line are one pure function
 * (`machineOptionsFor`), unit-locked, so this component decides nothing.
 *
 * Re-opening a job that already has a pending move EDITS that move (one per
 * job, structurally — the caller passes `existing`). Anchored to the clicked
 * element; closes on outside click or Escape; a hub refusal (named) renders
 * inside the popover and the move stays open for correction.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import type { ApprovedMachine, Placement, PlacementKind } from "../../boardTypes.js";
import { jobLabel } from "./laneMath.js";
import { STRATEGIES, bestFitIsNoOp, canRunOnLine, machineInSet, machineOptionsFor, type PlanJobs } from "./refineMath.js";

/** datetime-local wants "YYYY-MM-DDTHH:MM" in LOCAL time. */
function toLocalInput(iso: string | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export function MovePopover(props: {
  anchor: HTMLElement | null;
  job: { job: string; part: string | null };
  /** The machine the Refined row runs the job on. */
  planMachine: string;
  machines: string[];
  /** Amendment 4 — the clicked job's approved set as served (null = no set to offer from). */
  approved?: ApprovedMachine[] | null;
  /** Amendment 4 — whether the board's Approved machines card is connected at all. */
  bound?: boolean;
  /** Amendment 4 — served when the card is connected but its dataset can't be read. */
  unavailableReason?: string | null;
  planJobs: PlanJobs;
  basis: "optimized" | "plex" | "refined";
  pending: readonly Placement[];
  /** The job's existing pending move, when re-opened. */
  existing: Placement | null;
  /** Pre-select a strategy (the pending table's inline How switch hands off here for parameters). */
  initialKind?: PlacementKind;
  busy: boolean;
  onQueue: (move: Placement) => Promise<string | null>;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [kind, setKind] = useState<PlacementKind>(props.initialKind ?? props.existing?.kind ?? "best_fit");
  const [machine, setMachine] = useState<string>(props.existing?.machine ?? props.planMachine);
  const [anchorJob, setAnchorJob] = useState<string>(props.existing?.anchor_job ?? "");
  const [side, setSide] = useState<"before" | "after">(props.existing?.side ?? "after");
  const [at, setAt] = useState<string>(toLocalInput(props.existing?.at));
  const [error, setError] = useState<string | null>(null);
  const [flipUp, setFlipUp] = useState(false);

  useLayoutEffect(() => {
    const el = ref.current;
    const rect = props.anchor?.getBoundingClientRect();
    if (!el || !rect) return;
    const spaceBelow = window.innerHeight - rect.bottom - 8;
    setFlipUp(el.offsetHeight > spaceBelow && rect.top - 8 > spaceBelow);
  }, [props.anchor, kind]);

  useEffect(() => {
    function onDown(e: MouseEvent) {
      const t = e.target as Node | null;
      if (!t || ref.current?.contains(t) || props.anchor?.contains(t)) return;
      props.onClose();
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") props.onClose();
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [props]);

  const noOp = bestFitIsNoOp({ basis: props.basis, job: props.job.job, machineChoice: machine, planMachine: props.planMachine, pending: props.pending });
  const bound = props.bound === true;
  const { options: machineOptions, help: machineHelp, selectDisabled } = machineOptionsFor({
    part: props.job.part,
    approved: props.approved,
    planMachine: props.planMachine,
    viewMachines: props.machines,
    bound,
    unavailableReason: props.unavailableReason,
  });
  const canRunOn = bound ? canRunOnLine(props.approved, props.planMachine) : null;
  // The neighbor's machine decides the machine on before/after (the hub
  // resolves it too — the select simply follows the anchor here).
  const anchorMachine = anchorJob ? props.pending.find((p) => p.job === anchorJob)?.machine ?? props.planJobs.get(anchorJob)?.machine ?? null : null;
  const neighbors = [...props.planJobs.entries()].filter(([id]) => id !== props.job.job);

  const complete =
    kind === "best_fit" || kind === "run_next"
      ? !(kind === "best_fit" && noOp)
      : kind === "before_after"
        ? !!anchorJob
        : !!at;

  async function queue() {
    const move: Placement = { job: props.job.job, kind };
    const effectiveMachine = kind === "before_after" && anchorMachine ? anchorMachine : machine;
    if (effectiveMachine && effectiveMachine !== props.planMachine) move.machine = effectiveMachine;
    if (kind === "before_after") {
      move.anchor_job = anchorJob;
      move.side = side;
    }
    if (kind === "exact_time" || kind === "near_time") move.at = new Date(at).toISOString();
    setError(null);
    const refusal = await props.onQueue(move);
    if (refusal) setError(refusal);
    else props.onClose();
  }

  const rect = props.anchor?.getBoundingClientRect();
  const width = 320;
  const style: React.CSSProperties = rect
    ? {
        position: "fixed",
        top: flipUp ? Math.max(8, rect.top - (ref.current?.offsetHeight ?? 0) - 6) : rect.bottom + 6,
        left: Math.max(8, Math.min(window.innerWidth - width - 8, rect.left)),
        width,
      }
    : { display: "none" };

  return (
    <div ref={ref} className="boardMovePop" style={style} role="dialog" aria-label={`Move ${jobLabel(props.job)}`}>
      <div className="boardMovePopTitle">
        Move <span className="mono">{props.job.job}</span>
        {props.job.part ? <span className="muted"> ({props.job.part})</span> : null}
        <span className="muted"> · on {props.planMachine}</span>
        {canRunOn ? <span className="muted"> · {canRunOn}</span> : null}
      </div>
      <div className="boardMovePopStrategies" role="radiogroup" aria-label="How">
        {STRATEGIES.map((s) => {
          const disabled = s.kind === "best_fit" && noOp;
          return (
            <label key={s.kind} className={`boardMovePopStrategy${disabled ? " boardMovePopStrategyOff" : ""}`}>
              <input type="radio" name="strategy" checked={kind === s.kind} disabled={disabled} onChange={() => setKind(s.kind)} />
              <span>
                {s.label} <span className="muted">— {disabled ? "already at its best fit" : s.hint}</span>
              </span>
            </label>
          );
        })}
      </div>

      {kind === "before_after" ? (
        <div className="boardMovePopParams">
          <label className="field">
            <span className="label">Which job</span>
            <select className="select" value={anchorJob} onChange={(e) => setAnchorJob(e.target.value)}>
              <option value="">Choose…</option>
              {neighbors.map(([id, j]) => {
                // Amendment 4 — a neighbor sits on ITS machine, and "run beside
                // it" means going there: a neighbor on a machine this part is
                // not approved for is offered disabled, with the reason, rather
                // than accepted here and refused by the hub after the click.
                const neighborMachine = props.pending.find((p) => p.job === id)?.machine ?? j.machine;
                const notApproved = bound && machineInSet(props.approved, neighborMachine) === false;
                const runningBefore = j.running && side === "before";
                return (
                  <option key={id} value={id} disabled={runningBefore || notApproved}>
                    {jobLabel({ job: id, part: j.part })} · {neighborMachine}
                    {j.running ? " · running" : ""}
                    {notApproved ? " · not approved for this part" : ""}
                  </option>
                );
              })}
            </select>
          </label>
          <div className="boardMovePopSide" role="radiogroup" aria-label="Before or after">
            <label><input type="radio" name="side" checked={side === "before"} onChange={() => setSide("before")} /> before</label>
            <label><input type="radio" name="side" checked={side === "after"} onChange={() => setSide("after")} /> after</label>
          </div>
        </div>
      ) : null}
      {kind === "exact_time" || kind === "near_time" ? (
        <div className="boardMovePopParams">
          <label className="field">
            <span className="label">{kind === "exact_time" ? "Start exactly at" : "Not before"}</span>
            <input className="input" type="datetime-local" value={at} onChange={(e) => setAt(e.target.value)} />
          </label>
        </div>
      ) : null}

      <label className="field boardMovePopMachine">
        <span className="label">Machine</span>
        {kind === "before_after" && anchorMachine ? (
          <span className="muted">{anchorMachine} — follows the job it runs {side}</span>
        ) : (
          <select className="select" value={machine} disabled={selectDisabled} onChange={(e) => setMachine(e.target.value)}>
            {machineOptions.map((o) => (
              <option key={o.machine} value={o.machine} disabled={o.disabled}>{o.label}</option>
            ))}
          </select>
        )}
        <span className="help">{machineHelp}</span>
      </label>

      {error ? <div className="boardMovePopError" role="alert">{error}</div> : null}

      <div className="boardMovePopActions">
        <button type="button" className="btn btnSm" onClick={props.onClose}>Cancel</button>
        <button type="button" className="btn btnPrimary btnSm" onClick={() => void queue()} disabled={!complete || props.busy}>
          {props.existing ? "Update move" : "Queue move"}
        </button>
      </div>
    </div>
  );
}
